import {
  ChangeDetectorRef,
  Component,
  output,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { Attribute } from '@core/http-types';
import { TranslatePipe } from '@core/i18n-legacy';
import type { PaginationState } from '@core/ngrx-legacy';
import { AttrDataScope, TagsActions, tagsSelectors } from '@core/tags-and-sources-data';
import type {
  AttributesLoadMore,
  RtButtonVariant,
  RtInfoboxButtonVariant,
} from '@core/user-interface-legacy';
import {
  AttributesComponent,
  DropdownHeaderComponent,
  IconButtonComponent,
  IconComponent,
} from '@core/user-interface-legacy';
import type { Editable } from '@core/utils-legacy';

@Component({
  selector: 'rt-candidate-tags-typeahead-wrapper',
  templateUrl: './candidate-tags-typeahead-wrapper.component.html',
  styleUrls: ['./candidate-tags-typeahead-wrapper.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AttributesComponent,
    DropdownHeaderComponent,
    IconButtonComponent,
    RouterLink,
    TranslatePipe,
    IconComponent,
  ],
})
export class CandidateTagsTypeaheadWrapperComponent {
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly displayLimit = 20;

  public readonly withEmptyState = input<boolean>(false);
  public readonly createIconButtonVariant = input<RtButtonVariant | RtInfoboxButtonVariant>(
    'outline',
  );
  public readonly showMoreButtonVariant = input<RtButtonVariant | RtInfoboxButtonVariant>(
    'outline',
  );

  public readonly tags = input<
    Editable<
      Array<{
        value: string;
        processing?: boolean;
      }>
    >
  >(undefined!);

  public readonly limitItems = input<boolean>(false);

  public save = output<Attribute>();

  public delete = output<Attribute>();

  public tagsTypeahead: { isLoading: boolean; meta: PaginationState | null; rows: Attribute[] } = {
    isLoading: false,
    meta: null,
    rows: [],
  };

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    const isPending$ = this.store.select(
      tagsSelectors.isPending({
        scope: AttrDataScope.Typeahead,
      }),
    );
    const paginatedAttributes$ = this.store.select(
      tagsSelectors.paginatedAttributeSelector({
        scope: AttrDataScope.Typeahead,
      }),
    );

    combineLatest([paginatedAttributes$, isPending$])
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(([paginatedAttributes, isPending]) => {
        this.tagsTypeahead = {
          ...paginatedAttributes,
          rows: paginatedAttributes.rows.filter(
            ({ value }) =>
              !this.tags()
                .value?.map(tags => tags.value)
                .includes(value),
          ),
          isLoading: isPending,
        };
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public tagTypeaheadUpdate(query: string): void {
    const params = {
      query,
      page: 1,
      limit: 100,
    };

    this.store.dispatch(
      TagsActions.fetchPaginatedCollection({
        payload: {
          params,
          scope: AttrDataScope.Typeahead,
        },
      }),
    );
  }

  public tagLoadMore(params: AttributesLoadMore): void {
    this.store.dispatch(
      TagsActions.fetchPaginatedCollection({
        payload: { params, scope: AttrDataScope.Typeahead, add: true },
      }),
    );
  }
}
